import type {GroupableTicket} from '../types/groupableTicket';

/**
 * When a ticket left the queue, as an ISO timestamp, or null if it hasn't.
 *
 * `resolved_at` is the authoritative resolved moment and the only one that covers
 * the `resolved` status: `closed_at` is stamped only on the transition INTO
 * `closed`, and `resolved` / `closed` are distinct states — a ticket can sit in
 * `resolved` forever with `closed_at = NULL`. Keying resolution time on
 * `closed_at` alone would therefore drop every resolved-but-not-closed ticket
 * from the median while still counting it in the same header's "% resolved",
 * so the two subtotals would disagree about the same rows.
 *
 * `closed_at` remains the fallback for ticket shapes that predate `resolved_at`.
 */
export function resolvedMoment(ticket: GroupableTicket): string | null {
	return ticket.resolved_at ?? ticket.closed_at ?? null;
}

/**
 * Returns the median resolution time in **seconds** across the tickets that have
 * a resolved moment. Tickets still in the queue are excluded rather than counted
 * as zero, which would drag the median down as the queue grows.
 *
 * Seconds (not minutes) because the totals row formats durations with
 * `formatSlaDuration`, which is seconds-based — one unit across the dashboard
 * means a group subtotal and the totals row can never disagree.
 *
 * A pre-computed `resolution_time_seconds` wins when present: the backend derives
 * it with SLA business-hours rules that a wall-clock subtraction here cannot
 * reproduce. Wall-clock elapsed is the fallback. Anomalous rows where the
 * resolved moment is at or before creation clamp to zero rather than dropping
 * out, so every resolved ticket occupies exactly one median position regardless
 * of data quality.
 */
export function medianResolutionSeconds(tickets: readonly GroupableTicket[]): number | null {
	const durations: number[] = [];

	for (const ticket of tickets) {
		if (ticket.resolution_time_seconds != null) {
			durations.push(Math.max(0, ticket.resolution_time_seconds));
			continue;
		}
		const resolved = resolvedMoment(ticket);
		if (resolved === null) continue;
		const elapsed = (new Date(resolved).getTime() - new Date(ticket.created_at).getTime()) / 1000;
		// An unparseable timestamp yields NaN, which would poison the sort and make
		// the median NaN for the whole group. Skip the row instead.
		if (Number.isNaN(elapsed)) continue;
		durations.push(Math.max(0, elapsed));
	}

	if (durations.length === 0) return null;

	durations.sort((a, b) => a - b);
	const mid = Math.floor(durations.length / 2);
	return durations.length % 2 === 1 ? durations[mid]! : (durations[mid - 1]! + durations[mid]!) / 2;
}
